// src/components/Calendar/CalendarView.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import koLocale from '@fullcalendar/core/locales/ko';

import ScheduleModal from './ScheduleModal';
import CalendarHeader from './CalendarHeader';
import EventDetailPopover from './EventDetailPopover';
import CalendarVoteDialog from './CalendarVoteDialog';

import {
    Box,
    Typography,
    Card,
    useTheme,
    GlobalStyles,
    useMediaQuery,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HowToVoteIcon from '@mui/icons-material/HowToVote';

import api from '../../api/axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
dayjs.locale('ko');

export default function CalendarView() {
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width:600px)');
    const calendarRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [events, setEvents] = useState([]);
    const [title, setTitle] = useState('');
    const [isEdit, setIsEdit] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [detail, setDetail] = useState(null);
    const [voteDialog, setVoteDialog] = useState({ open: false, schedule: null });
    // 🔍 필터 (all | schedule | content) 
    const [filter, setFilter] = useState('all');
    // 🔍 원본 이벤트 보관 
    const [rawEvents, setRawEvents] = useState([]);

    // 주차 계산 함수
    const getWeekInfo = (date) => {
        const startOfWeek = dayjs(date).startOf('week');
        const endOfWeek = dayjs(date).endOf('week');
        const offset = dayjs(date).startOf('month').day();
        const day = date.date() + offset;
        const week = Math.ceil(day / 7);
        return {
            week,
            start: startOfWeek.format('MM.DD'),
            end: endOfWeek.format('MM.DD'),
            month: date.format('M'),
        };
    };

    const [currentWeek, setCurrentWeek] = useState(getWeekInfo(dayjs()));

    const fetchEvents = useCallback(() => {
        api.get('/api/schedules/').then((res) => {
            const mapped = res.data.map((i) => {
                const start = dayjs(`${i.date}T${i.time}`);
                const end =
                    i.end_date && i.end_time
                        ? dayjs(`${i.end_date}T${i.end_time}`)
                        : start.add(2, 'hour');

                return {
                    id: String(i.id),
                    title: '',
                    start: start.toISOString(),
                    end: end.toISOString(),
                    display: 'block',
                    backgroundColor:
                        i.content_type === 'internal'
                            ? `${i.color}22`
                            : theme.palette.primary.main + '22',
                    extendedProps: {
                        owner: i.owner,
                        type: i.type,
                        time: i.time,
                        urgent: i.urgent,
                        people: i.people,
                        location: i.location,
                        end_date: i.end_date,
                        end_time: i.end_time,
                        title: i.title,
                        content_type: i.content_type,
                        color: i.color,
                        vote: i.vote ?? null,
                    },
                };
            });
            setRawEvents(mapped);   // 전체 저장 
            setEvents(mapped);      // 최초 표시
        });
    }, [theme.palette.primary.main]);

    useEffect(() => {
        let filtered = [...rawEvents];

        if (filter === 'schedule') {
            // 일정 = 내부가 **아닌** 것 
            filtered = filtered.filter(
                (e) => e.extendedProps.content_type !== 'internal'
            );
        } else if (filter === 'content') {
            // 컨텐츠 = 내부(internal) 만 
            filtered = filtered.filter(
                (e) => e.extendedProps.content_type === 'internal'
            );
        }
        setEvents(filtered);
    }, [filter, rawEvents]);

    useEffect(() => {
        api.get('/api/user-status/').then((res) => {
            setCurrentUser(res.data.username);
            setUserRole(res.data.role);
        });
        fetchEvents();
    }, [fetchEvents]);

    useEffect(() => {
        const api = calendarRef.current?.getApi();
        if (api) {
            queueMicrotask(() => {
                api.changeView(isMobile ? 'listWeek' : 'dayGridMonth');
            });
        }
    }, [isMobile]);

    useEffect(() => {
        const listener = (e) => {
            const updated = e.detail;
            setEvents((prev) =>
                prev.map((ev) =>
                    ev.id === String(updated.id)
                        ? {
                            ...ev,
                            extendedProps: { ...ev.extendedProps, vote: updated.vote },
                        }
                        : ev
                )
            );
        };
        window.addEventListener('schedule-updated', listener);
        return () => window.removeEventListener('schedule-updated', listener);
    }, []);

    const handleDateClick = (arg) => {
        setSelectedDate(arg.dateStr);
        setIsEdit(false);
        setEditTarget(null);
        setOpen(true);
    };

    const handleEventClick = ({ event, el }) => {
        const { id, start, end, extendedProps } = event;
        const {
            owner,
            location,
            type,
            urgent,
            people,
            content_type,
            title: evTitle,
            vote,
        } = extendedProps;

        const isOwner = owner === currentUser;
        const isAdmin = ['admin', 'master'].includes(userRole);
        const startTime = dayjs(start);
        const endTime = end ? dayjs(end) : startTime.add(2, 'hour');

        setDetail({
            id: String(id),
            host: owner,
            startDate: startTime.format('YYYY년 M월 D일'),
            startTime: startTime.format('A h:mm'),
            endDate: endTime.format('YYYY년 M월 D일'),
            endTime: endTime.format('A h:mm'),
            type,
            urgent,
            people,
            location,
            content_type,
            title: evTitle,
            vote,
            editable: isOwner || isAdmin,
        });
        setAnchorEl(el);
    };

    const handleEditFromPopover = (id) => {
        const event = events.find((e) => e.id === String(id));
        if (!event) return;
        const { start, extendedProps } = event;
        const { type, urgent, people, end_date, end_time, location, vote } =
            extendedProps;

        setEditTarget({
            id,
            date: dayjs(start).format('YYYY-MM-DD'),
            time: dayjs(start).format('HH:mm'),
            endDate: end_date || dayjs(start).format('YYYY-MM-DD'),
            endTime: end_time || dayjs(start).add(2, 'hour').format('HH:mm'),
            type,
            urgent,
            people,
            location: location || '',
            title: extendedProps.title || '',
            content_type: extendedProps.content_type || 'schedule',
            color: extendedProps.color || '#1976d2',
            vote,
        });

        setAnchorEl(null);
        setTimeout(() => {
            setIsEdit(true);
            setOpen(true);
        }, 0);
    };

    const handleSaveEvent = (payload, edit = false, id = null) => {
        const req =
            edit && id
                ? api.patch(`/api/schedules/${id}/`, payload)
                : api.post('/api/schedules/', payload);
        req.then(() => {
            fetchEvents();
            closeModal();
        });
    };

    const handleDeleteEvent = (id) => {
        if (window.confirm('정말 삭제하시겠습니까?')) {
            api.delete(`/api/schedules/${id}/`).then(() => {
                fetchEvents();
                closeModal();
            });
        }
    };

    const closeModal = () => {
        setOpen(false);
        setIsEdit(false);
        setEditTarget(null);
        setAnchorEl(null);
        setDetail(null);
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <GlobalStyles
                styles={{
                    '.fc .fc-more-link': {
                        display: 'inline-block',
                        padding: '6px 20px',
                        borderRadius: 6,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        backgroundColor: '#a5d6a7',
                        color: '#000',
                        transition: 'background-color 0.2s',
                    },
                    '.fc .fc-more-link:hover': { backgroundColor: '#81c784' },
                    '.fc-theme-standard th': {
                        backgroundColor: '#fafafa',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        padding: '12px 0',
                    },
                    '.fc .fc-day-today': { backgroundColor: '#fffde7 !important' },
                    '.fc .fc-daygrid-row': { minHeight: '140px' },
                    '.fc-list': { fontSize: '0.95rem' },
                    '.fc-list-day-cushion': { backgroundColor: '#e8f5e9' },
                    '@media (max-width:600px)': {
                        '.fc-toolbar-title': { fontSize: '1.1rem' },
                        '.fc-list-event-time': { display: 'none' },
                        '.fc-list-event-title': { width: '100%', paddingLeft: 6 },
                    },
                }}
            />

            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CalendarMonthIcon
                        sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 1 }}
                    />
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        산들 일정
                    </Typography>
                </Box>

                {isMobile && (
                    <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            📆 현재 보기: {currentWeek.month}월 {currentWeek.week}주차 · {currentWeek.start} ~ {currentWeek.end}
                        </Typography>
                    </Box>
                )}
            </Box>

            <CalendarHeader
                calendarRef={calendarRef}
                title={title}
                onAddClick={() => {
                    setSelectedDate(dayjs().format('YYYY-MM-DD'));
                    setIsEdit(false);
                    setEditTarget(null);
                    setOpen(true);
                }}
                filter={filter}          // 🔍 추가
                setFilter={setFilter}    // 🔍 추가
            />

            <Card elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
                    locale={koLocale}
                    initialView={isMobile ? 'listWeek' : 'dayGridMonth'}
                    headerToolbar={false}
                    titleFormat={{ year: 'numeric', month: 'long' }}
                    dayHeaderFormat={{ weekday: 'short' }}
                    dayCellContent={(arg) => arg.dayNumberText}
                    datesSet={(info) => {
                        setTitle(info.view.title);
                        setCurrentWeek(getWeekInfo(dayjs(info.start)));
                    }}
                    aspectRatio={isMobile ? 0.7 : 1.4}
                    events={events}
                    dateClick={handleDateClick}
                    eventClick={handleEventClick}
                    eventDisplay="block"
                    dayMaxEventRows={4}
                    height="auto"
                    eventColor={theme.palette.primary.main + '22'}
                    eventTextColor={theme.palette.text.primary}
                    eventContent={(arg) => {
                        const {
                            owner,
                            type,
                            time,
                            title: evTitle,
                            content_type,
                            vote,
                        } = arg.event.extendedProps;

                        const isList = arg.view.type.includes('list');
                        const t = dayjs(`2024-01-01T${time}`).format('A h:mm');

                        if (isMobile && isList) {
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', px: 1, py: 0.5 }}>
                                    <Typography variant="body2" fontWeight={700}>{owner}</Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2">{t}</Typography>
                                        {vote && (
                                            <HowToVoteIcon fontSize="medium" sx={{ color: theme.palette.primary.main, ml: 1 }} />
                                        )}
                                    </Box>
                                    <Typography variant="body2">
                                        {content_type === 'internal' ? evTitle : type}
                                    </Typography>
                                </Box>
                            );
                        }

                        return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                {vote && (
                                    <HowToVoteIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                )}
                                <span>
                                    <b>{owner}</b> - {t} {content_type === 'internal' ? evTitle : type}
                                </span>
                            </div>
                        );
                    }}
                />
            </Card>

            <EventDetailPopover
                anchorEl={anchorEl}
                detail={detail}
                onClose={() => setAnchorEl(null)}
                onEdit={handleEditFromPopover}
                onDelete={handleDeleteEvent}
                onVote={(sched) => {
                    setAnchorEl(null);
                    setVoteDialog({ open: true, schedule: sched });
                }}
            />

            <ScheduleModal
                open={open}
                onClose={closeModal}
                selectedDate={selectedDate}
                onAdd={handleSaveEvent}
                isEdit={isEdit}
                initialData={editTarget}
                isAdmin={['admin', 'master'].includes(userRole)}
            />

            <CalendarVoteDialog
                open={voteDialog.open}
                onClose={() => setVoteDialog({ open: false, schedule: null })}
                schedule={voteDialog.schedule}
            />
        </Box>
    );
}
